import type {
  PlaceholderShapeKind,
  PlaceholderShapeParams,
  RawSlotMeta,
  ResolvedPlaceholderShape,
} from "./types";

const KINDS: Set<string> = new Set([
  "rect",
  "roundedRect",
  "ellipse",
  "hexagon",
  "star",
  "rhombus",
  "heart",
  "parallelogram",
]);

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.max(lo, Math.min(hi, n));
}

function defaultRoundedness(kind: PlaceholderShapeKind): number {
  switch (kind) {
    case "hexagon":
      return 0.25;
    case "star":
      return 0.2;
    case "parallelogram":
      return 0.15;
    default:
      return 0;
  }
}

/** Derive normalized roundedness from legacy border-radius percent string. */
export function roundednessFromLegacyBorderRadiusPercent(
  value: string | undefined
): number {
  if (!value || !value.endsWith("%")) return 0.2;
  const pct = Number(value.replace("%", ""));
  if (!Number.isFinite(pct)) return 0.2;
  return clamp((2 * pct) / 100, 0, 1);
}

export function coerceShapeKind(v: unknown): PlaceholderShapeKind | null {
  if (typeof v !== "string") return null;
  return KINDS.has(v) ? (v as PlaceholderShapeKind) : null;
}

export function resolveShapeParams(
  kind: PlaceholderShapeKind,
  params: PlaceholderShapeParams | undefined
): ResolvedPlaceholderShape["params"] {
  const base = defaultRoundedness(kind);
  const p = params ?? {};
  const starPoints = clamp(
    Math.round(p.starPoints ?? 5),
    5,
    12
  );
  /** Product standard: always cover (slot filled; pinch/zoom on select for customers). */
  const fit = "cover" as const;
  let waviness = clamp(p.waviness ?? 0, 0, 1);
  if (!Number.isFinite(waviness)) waviness = 0;
  let roundedness = clamp(p.roundedness ?? base, 0, 1);
  if (!Number.isFinite(roundedness)) roundedness = base;
  let skew = clamp(
    p.skew !== undefined && p.skew !== null
      ? p.skew
      : kind === "parallelogram"
      ? 0.44
      : 0,
    -1,
    1
  );
  if (!Number.isFinite(skew)) skew = kind === "parallelogram" ? 0.44 : 0;

  let wavinessByEdge = p.wavinessByEdge;
  if (wavinessByEdge && !Array.isArray(wavinessByEdge)) {
    wavinessByEdge = undefined;
  }

  return {
    roundedness,
    waviness,
    wavinessByEdge,
    starPoints,
    skew,
    fit,
  };
}

/**
 * Infer canonical shape from legacy `apertureType` / `apertureMask` when `shape` is absent.
 */
export function inferShapeFromLegacy(meta: RawSlotMeta): ResolvedPlaceholderShape {
  const t = meta.apertureType;
  const mask = meta.apertureMask;
  if (t === "circle" || t === "ellipse") {
    return { kind: "ellipse", params: resolveShapeParams("ellipse", {}) };
  }
  if (t === "rounded" && mask?.type === "border-radius") {
    const rn = roundednessFromLegacyBorderRadiusPercent(mask.value);
    return {
      kind: "rect",
      params: resolveShapeParams("rect", { roundedness: rn }),
    };
  }
  return { kind: "rect", params: resolveShapeParams("rect", {}) };
}

/** Full normalization: never throws; invalid input → rect. */
export function normalizeSlotShape(meta: RawSlotMeta | undefined | null): {
  kind: PlaceholderShapeKind;
  params: ResolvedPlaceholderShape["params"];
} {
  if (!meta || typeof meta !== "object") {
    return {
      kind: "rect",
      params: resolveShapeParams("rect", {}),
    };
  }
  const shape = meta.shape;
  if (shape && typeof shape === "object" && shape.kind) {
    let k = coerceShapeKind(shape.kind);
    if (k) {
      if (k === "roundedRect") k = "rect";
      return { kind: k, params: resolveShapeParams(k, shape.params) };
    }
  }
  return inferShapeFromLegacy(meta);
}

/** Optional: keep legacy aperture fields in sync for old readers. */
export function legacyApertureFromShape(
  kind: PlaceholderShapeKind,
  params: ResolvedPlaceholderShape["params"]
): Pick<RawSlotMeta, "apertureType" | "apertureMask"> {
  switch (kind) {
    case "ellipse":
      return {
        apertureType: "ellipse",
        apertureMask: undefined,
      };
    case "rect":
    case "roundedRect": {
      if (params.roundedness > 0.02) {
        const pct = Math.round(clamp(params.roundedness / 2, 0, 0.5) * 100);
        return {
          apertureType: "rounded",
          apertureMask: {
            type: "border-radius",
            value: `${clamp(pct, 0, 50)}%`,
          },
        };
      }
      return { apertureType: "rect", apertureMask: undefined };
    }
    default:
      return { apertureType: "rect", apertureMask: undefined };
  }
}
